import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Check, X, Star, MessageSquare, Loader2, Clock } from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const StarRow = ({ rating }) => (
  <div className="flex">
    {[1, 2, 3, 4, 5].map((star) => (
      <Star
        key={star}
        className={`w-4 h-4 ${star <= rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`}
      />
    ))}
  </div>
);

export const AdminReviewsTab = ({ pendingReviews, approvedReviews, token, onRefresh }) => {
  const [processingReview, setProcessingReview] = useState(null);

  const handleReviewAction = async (reviewId, action) => {
    setProcessingReview(reviewId);
    try {
      await axios.post(`${API}/reviews/admin/${action}/${reviewId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Review ${action}d successfully`);
      onRefresh();
    } catch (error) {
      toast.error(`Failed to ${action} review`);
    } finally {
      setProcessingReview(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Pending Reviews Section */}
      <Card className="glass-card rounded-2xl border-0">
        <CardContent className="p-6">
          <h2 className="font-heading text-xl font-semibold text-foreground mb-6 flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-500" />
            Pending Reviews
            {pendingReviews.length > 0 && (
              <span className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 px-2 py-1 rounded-full">
                {pendingReviews.length}
              </span>
            )}
          </h2>

          {pendingReviews.length === 0 ? (
            <div className="text-center py-8">
              <MessageSquare className="w-10 h-10 mx-auto text-muted-foreground/50 mb-3" />
              <p className="text-muted-foreground">No pending reviews to approve.</p>
              <p className="text-sm text-muted-foreground/70 mt-1">New reviews will appear here for approval.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingReviews.map((review) => (
                <div key={review.id} className="bg-muted/30 rounded-xl p-5" data-testid={`review-${review.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-foreground">{review.display_name}</span>
                        <StarRow rating={review.rating} />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Submitted {new Date(review.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  <h4 className="font-medium text-foreground mb-2">"{review.title}"</h4>
                  <p className="text-sm text-muted-foreground mb-4">{review.content}</p>

                  <div className="flex gap-3">
                    <Button
                      size="sm"
                      onClick={() => handleReviewAction(review.id, 'approve')}
                      disabled={processingReview === review.id}
                      className="bg-green-600 hover:bg-green-700 text-white rounded-lg"
                      data-testid={`approve-review-${review.id}`}
                    >
                      {processingReview === review.id ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-1" />
                      ) : (
                        <Check className="w-4 h-4 mr-1" />
                      )}
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleReviewAction(review.id, 'reject')}
                      disabled={processingReview === review.id}
                      className="text-red-600 border-red-200 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                      data-testid={`reject-review-${review.id}`}
                    >
                      <X className="w-4 h-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Approved Reviews Section */}
      <Card className="glass-card rounded-2xl border-0">
        <CardContent className="p-6">
          <h2 className="font-heading text-xl font-semibold text-foreground mb-6 flex items-center gap-2">
            <Check className="w-5 h-5 text-green-500" />
            Approved Reviews (Public)
            {approvedReviews.length > 0 && (
              <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 px-2 py-1 rounded-full">
                {approvedReviews.length}
              </span>
            )}
          </h2>

          {approvedReviews.length === 0 ? (
            <div className="text-center py-8">
              <Star className="w-10 h-10 mx-auto text-muted-foreground/50 mb-3" />
              <p className="text-muted-foreground">No approved reviews yet.</p>
              <p className="text-sm text-muted-foreground/70 mt-1">Approved reviews will appear on the landing page as testimonials.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {approvedReviews.map((review) => (
                <div key={review.id} className="bg-green-50/50 dark:bg-green-900/10 rounded-xl p-5 border border-green-100 dark:border-green-900/30">
                  <div className="flex items-center gap-2 mb-3">
                    <StarRow rating={review.rating} />
                    {review.is_featured && (
                      <span className="text-xs bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300 px-2 py-0.5 rounded-full">
                        Featured
                      </span>
                    )}
                  </div>
                  <h4 className="font-medium text-foreground mb-2">"{review.title}"</h4>
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-3">{review.content}</p>
                  <p className="text-xs text-muted-foreground">— {review.display_name}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
